/* global window */
import * as React from "react";
import { useState, useEffect, useLayoutEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { getPinsForMoving } from "../../helpers/grid";
import { select } from "../../state/selectionSlice";
import StructureMenuItem from "../contextmenus/menuitems/StructureMenuItem";
import { HandleSlot, HandleSlotRollover } from "../../assets/icons";
import { setLastOpenAccordionItem } from "../../state/appStateSlice";

const InsertDot = (props) => {
  const [onHovered, setOnHovered] = useState(false);
  const [onAreaHovered, setOnAreaHovered] = useState(false);
  const [mouseOverDocument, setMouseOverDocument] = useState(false);
  const cols = useSelector((state) => state.gridd.present.structure.columns);
  const rows = useSelector((state) => state.gridd.present.structure.rows);
  const selection = useSelector((state) => state.selection);
  const borders = useSelector((state) => state.gridd.present.borders);
  const app = useSelector((state) => state.app);
  const dispatch = useDispatch();
  const [menuPosition, setMenuPosition] = useState(null);

  // Check if there's a border at the current position
  const getBorderAtPosition = () => {
    return borders.find((border) => {
      if (props.vertical) {
        // For vertical dots (column separators), check for vertical borders
        return border.isVertical && border.index === props.index;
      } else {
        // For horizontal dots (row separators), check for horizontal borders
        return !border.isVertical && border.index === props.index;
      }
    });
  };

  const borderAtPosition = getBorderAtPosition();
  const shouldShowBorderMenu = !!borderAtPosition;

  const hasParent = (element, id) => {
    if (element?.parentNode) {
      if (typeof element.parentNode.id === "string" && element.parentNode.id === id) return true;
      else return hasParent(element.parentNode, id);
    }
    return false;
  };

  useEffect(() => {
    function handleMouseMove(e) {
      setMouseOverDocument(true);
      if (app.preferencesDialogOpen || app.imageCropDialogOpen || app.styleSheetDialogOpen) {
        setOnAreaHovered(false);
        return;
      }
      if (selection.borders[0]?.isVertical === props.vertical && selection.borders[0]?.index === props.index) {
        setOnAreaHovered(false);
        return;
      }

      if (e.target?.localName === "button") return;
      if (typeof e.target?.id === "string" && e.target.id.indexOf("tooltip") >= 0) return;

      if (e.target?.localName === "div" || e.target?.localName === "span") {
        if (typeof e.target.id === "string") {
          if (e.target.id === "context_menu_left_div") return;
          if (e.target.id === "context_menu_right_div") return;
          if (e.target.id === "context_menu_grid_div") return;
          if (e.target.id === "context_menu_parent_div") return;
        }
        const hp = hasParent(e.target, "context_menu_parent_div");
        if (hp) return; // excluding potentially the menu tab nodes
        const hp2 = hasParent(e.target, "context_menu_item_structure");
        if (hp2) return;
        const hp3 = hasParent(e.target, "gridd_menu_popover");
        if (hp3) return; // excluding bottom right Gridd menu
      }

      const sensitivity = 5;
      if (props.vertical) {
        const correctedX = e.clientX - 24;
        const pins = getPinsForMoving(cols, window.innerWidth);

        if (correctedX > pins[props.index] - sensitivity && correctedX < pins[props.index] + sensitivity) {
          setOnAreaHovered(true);
        } else {
          setOnAreaHovered(false);
        }
      } else {
        const correctedY = e.clientY - 24;
        const pins = getPinsForMoving(rows, window.innerHeight);
        if (correctedY > pins[props.index] - sensitivity && correctedY < pins[props.index] + sensitivity) {
          setOnAreaHovered(true);
        } else {
          setOnAreaHovered(false);
        }
      }
    }
    window.addEventListener("mousemove", handleMouseMove);
    function onMouseUp(e) {
      // Ignore clicks inside the menu content so its buttons can be clicked
      if (
        typeof e.target.id === "string" &&
        (e.target.id === "context_menu_item_structure" || hasParent(e.target, "context_menu_item_structure"))
      ) {
        return;
      }
      if (onAreaHovered) {
        if (typeof e.target.id === "string" && e.target.id === "context_menu_parent_div") return;
        const hp = hasParent(e.target, "context_menu_parent_div");
        if (hp) return;

        if (dotRef.current) {
          const rect = dotRef.current.getBoundingClientRect();
          setMenuPosition({ left: rect.right + 4, top: rect.top });
        }
      } else {
        setMenuPosition(null);
      }
    }
    window.addEventListener("mouseup", onMouseUp);
    function handleMouseLeave() {
      setMouseOverDocument(false);
    }
    window.addEventListener("mouseout", handleMouseLeave);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      window.removeEventListener("mouseout", handleMouseLeave);
    };
  }, [onAreaHovered, props.vertical, props.index, borders, cols, rows, selection, app]);

  // Clear highlight when selection slot is cleared (e.g., after inserting a column via menu)
  React.useEffect(() => {
    if (selection.slot === null) {
      setOnAreaHovered(false);
      setOnHovered(false);
      setMenuPosition(null);
    }
  }, [selection.slot]);

  // Add a ref to the dot
  const dotRef = React.useRef();
  // Add a ref to the menu container
  const menuRef = React.useRef();

  // Adjust menu position if it would render outside the viewport
  useLayoutEffect(() => {
    if (menuPosition && menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      let newLeft = menuPosition.left;
      let newTop = menuPosition.top;
      const margin = 8;
      if (newLeft + rect.width > window.innerWidth) {
        newLeft = window.innerWidth - rect.width - margin;
      }
      if (newTop + rect.height > window.innerHeight) {
        newTop = window.innerHeight - rect.height - margin;
      }
      if (newLeft < margin) newLeft = margin;
      if (newTop < margin) newTop = margin;
      if (newLeft !== menuPosition.left || newTop !== menuPosition.top) {
        setMenuPosition({ left: newLeft, top: newTop });
      }
    }
  }, [menuPosition]);

  return (
    <>
      {/* Dot component */}
      <div
        style={{
          position: "absolute",
          left: props.vertical ? -8 : props.isLast ? -3 : 5,
          top: props.vertical ? 7 : -7.5,
          zIndex: 6,
        }}
        onMouseEnter={() => {
          setOnHovered(true);
        }}
        onMouseLeave={() => {
          setOnHovered(false);
        }}
      >
        <div
          ref={dotRef}
          id={`insert_dot_${props.vertical ? "vertical" : "horizontal"}_${props.index}`}
          onClick={() => {
            if (dotRef.current) {
              const rect = dotRef.current.getBoundingClientRect();
              setMenuPosition({ left: rect.right + 4, top: rect.top });
            }

            // If there's a border at this position, select the border
            if (borderAtPosition) {
              dispatch(setLastOpenAccordionItem("structure"));
              dispatch(
                select({
                  border: {
                    id: borderAtPosition.id,
                    index: props.index,
                    isVertical: props.vertical,
                  },
                })
              );
            } else {
              // Otherwise, select the slot for insertion
              dispatch(select({ slot: { vertical: props.vertical, index: props.index } }));
            }
          }}
          style={{
            position: "relative",
          }}
        >
          {mouseOverDocument ? (
            onHovered ? (
              <HandleSlotRollover style={{ width: 16, height: 16 }} />
            ) : (
              <HandleSlot style={{ width: 16, height: 16 }} />
            )
          ) : (
            <></>
          )}
        </div>
      </div>

      {/* Menu rendered outside the dot's stacking context */}
      {!shouldShowBorderMenu &&
      selection.slot?.vertical == props.vertical &&
      selection.slot?.index == props.index &&
      menuPosition ? (
        <div
          style={{
            pointerEvents: "none",
            position: "fixed",
            left: menuPosition.left,
            top: menuPosition.top,
            zIndex: 2000,
          }}
        >
          <div
            ref={menuRef}
            style={{
              background: "white",
              pointerEvents: "all",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            }}
          >
            <StructureMenuItem />
          </div>
        </div>
      ) : null}
    </>
  );
};

InsertDot.propTypes = {
  vertical: PropTypes.bool.isRequired,
  index: PropTypes.number.isRequired,
  isLast: PropTypes.bool,
};

export default InsertDot;
